import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { ModuleName } from "@dentacore/shared-types";

import { recordHelpQuestion } from "../api/help";
import { useCanAny } from "../auth/use-can";
import { useModules } from "../shell/modules-context";
import { HELP_ARTICLES } from "./articles";
import type { HelpArticle } from "./articles";
import { helpForPage, markHits, searchHelp } from "./search";
import styles from "./help-panel.module.css";

/**
 * The help panel — a place to ask, answered from the help articles, never by
 * a model (help-panel grill, 2026-11-02).
 *
 * Opened from the top bar's ?, it sits beside the page rather than over it, so
 * the person can follow the steps on the screen they are reading about.
 *
 * **Fitted to the reader.** An article about an add-on the clinic lacks says
 * so; one for a page the reader cannot open says who does it, with no button.
 * Nobody is handed steps the server would refuse.
 */

/** How long the typing rests before an unanswered question is noted. */
const NOTE_AFTER_MS = 1500;

const MODULE_LABELS: Record<ModuleName, string> = {
  SUPPLY: "Supply",
  ACCOUNTING: "Accounting",
  CHATBOT: "Chatbot",
};

export function HelpPanel({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { pathname } = useLocation();
  const [question, setQuestion] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const asked = question.trim();
  const matches = useMemo(() => searchHelp(HELP_ARTICLES, asked), [asked]);
  // A tall window has room for more suggestions; a short one keeps five.
  const room = window.innerHeight >= 900 ? 10 : 5;
  const onPage = useMemo(() => helpForPage(HELP_ARTICLES, pathname, room), [pathname, room]);

  // A new question opens its best answer; the reader's own choice wins after.
  useEffect(() => {
    setOpenId(matches[0]?.article.id ?? null);
  }, [matches]);

  const list = asked === "" ? onPage : matches.map((match) => match.article);
  const nothing = asked !== "" && matches.length === 0;
  const hitsOf = useMemo(
    () => new Map(matches.map((match) => [match.article.id, match.hits])),
    [matches],
  );

  /**
   * **A question nothing answers is noted, once** (HP-D7). Not on every
   * keystroke — "sms", "sms t", "sms th" are one question being typed — so it
   * waits until the typing has rested, or Enter is pressed. The same words are
   * noted once per opening of the panel. A failure is swallowed: the reader
   * asked for help, not for an error about the help.
   */
  const noted = useRef(new Set<string>());
  const note = useCallback(
    (words: string): void => {
      const key = words.toLocaleLowerCase();
      if (noted.current.has(key)) return;
      noted.current.add(key);
      void recordHelpQuestion(words.slice(0, 200), pathname).catch(() => undefined);
    },
    [pathname],
  );
  useEffect(() => {
    if (!nothing || asked.length < 3) return undefined;
    const timer = window.setTimeout(() => note(asked), NOTE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [nothing, asked, note]);

  return (
    <aside className={styles.panel} aria-label="Help" data-testid="help-panel">
      <div className={styles.head}>
        <h2 className={styles.title}>Help</h2>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close help"
          title="Close help"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <form
        className={styles.ask}
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          // Enter means "that is my question" — note it without waiting.
          if (nothing && asked.length >= 3) note(asked);
        }}
      >
        <input
          ref={input}
          type="search"
          value={question}
          onChange={(event) => {
            setQuestion(event.target.value);
          }}
          placeholder="Ask a question…"
          aria-label="Ask a question"
          data-testid="help-question"
        />
      </form>

      {nothing && (
        <div className={styles.nothing} role="status" data-testid="help-nothing">
          <p className={styles.nothingTitle}>Nothing in the help covers that yet.</p>
          <p className={styles.quiet}>We&rsquo;ve noted the question so it can be added.</p>
        </div>
      )}

      {(asked === "" || nothing) && onPage.length > 0 && (
        <p className={styles.section}>{nothing ? "Help for this page" : "On this page"}</p>
      )}
      {asked !== "" && !nothing && (
        <p className={styles.section}>
          {matches.length === 1 ? "Best match" : `${String(matches.length)} matches`}
        </p>
      )}

      <ol className={styles.list}>
        {(nothing ? onPage : list).map((article) => (
          <Card
            key={article.id}
            article={article}
            hits={nothing ? undefined : hitsOf.get(article.id)}
            open={openId === article.id}
            onToggle={() => {
              setOpenId((was) => (was === article.id ? null : article.id));
            }}
          />
        ))}
      </ol>

      {asked === "" && onPage.length === 0 && (
        <p className={styles.quiet}>Type a question — a few words is enough.</p>
      )}
    </aside>
  );
}

/** A line of an article with the words the question matched marked (help-panel comment, 2026-11-04). */
function Marked({
  text,
  hits,
}: {
  text: string;
  hits: ReadonlySet<string> | undefined;
}): React.JSX.Element {
  if (hits === undefined) return <>{text}</>;
  return (
    <>
      {markHits(text, hits).map((piece, i) =>
        piece.hit ? (
          <mark key={i} className={styles.hit} data-testid="help-hit">
            {piece.text}
          </mark>
        ) : (
          piece.text
        ),
      )}
    </>
  );
}

function Card({
  article,
  hits,
  open,
  onToggle,
}: {
  article: HelpArticle;
  /** The words the question matched; none when nothing was asked. */
  hits?: ReadonlySet<string> | undefined;
  open: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const navigate = useNavigate();
  const canAny = useCanAny();
  const { has } = useModules();
  const isAdmin = canAny(["settings.clinic.manage"]);

  const lacksModule = article.module !== undefined && !has(article.module);
  const lacksAccess = article.needs !== undefined && !canAny(article.needs);

  return (
    <li className={styles.card} data-open={open} data-testid="help-card">
      <button type="button" className={styles.cardHead} aria-expanded={open} onClick={onToggle}>
        <span className={styles.chevron} aria-hidden="true" />
        <span className={styles.cardTitle}>
          <Marked text={article.title} hits={hits} />
        </span>
      </button>

      {open && (
        <div className={styles.cardBody}>
          <p className={styles.answer}>
            <Marked text={article.answer} hits={hits} />
          </p>

          {lacksModule ? (
            <p className={styles.fit} data-testid="help-fit">
              Your clinic doesn&rsquo;t have {MODULE_LABELS[article.module as ModuleName]}.{" "}
              {isAdmin ? "Ask for it in System → Modules." : "Ask your administrator to add it."}
            </p>
          ) : lacksAccess ? (
            <p className={styles.fit} data-testid="help-fit">
              {article.whoDoesIt ?? "Someone with more access does this — ask an administrator."}
            </p>
          ) : (
            <>
              <ol className={styles.steps}>
                {article.steps.map((step) => (
                  <li key={step}>
                    <Marked text={step} hits={hits} />
                  </li>
                ))}
              </ol>
              {article.to !== undefined && (
                <button
                  type="button"
                  className={styles.go}
                  onClick={() => {
                    // The panel stays open: the steps are read beside the page.
                    navigate(article.to as string);
                  }}
                  data-testid="help-go"
                >
                  Take me there →
                </button>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}
